import * as React from 'react';

type Props = {
  children: any;
  innerStyle?: React.CSSProperties;
  open: boolean;
  shave?: number;
  style?: React.CSSProperties;
};

const Collapser = (props: Props) => {
  return (
    <div style={props.style}>
      <div
        style={{...props.innerStyle, display: props.open ? 'block' : 'none'}}
      >
        {props.children}
      </div>
    </div>
  );
};

type AnimatedCollapserProps = {
  children: React.ReactNode;
  open: boolean;
  duration?: number;
  style?: React.CSSProperties;
};

export const AnimatedCollapser = ({
  children,
  open,
  duration = 200,
  style,
}: AnimatedCollapserProps) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = React.useState<number | undefined>(
    open ? undefined : 0,
  );

  React.useEffect(() => {
    if (!contentRef.current) return;

    if (open) {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(contentHeight);

      // After animation completes, set to auto to handle dynamic content
      const timeout = setTimeout(() => {
        setHeight(undefined);
      }, duration);

      return () => clearTimeout(timeout);
    } else {
      // First set the explicit height, then animate to 0
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(contentHeight);

      // Use requestAnimationFrame to ensure the browser has painted the explicit height
      let animationFrame = requestAnimationFrame(() => {
        animationFrame = requestAnimationFrame(() => {
          setHeight(0);
        });
      });
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [open, duration]);

  return (
    <div
      style={{
        ...style,
        height: height === undefined ? 'auto' : height,
        overflow: 'hidden',
        transition: `height ${duration}ms ease-in-out`,
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
};

export default Collapser;
